import { useCallback, useEffect, useRef, useState } from 'react';
import { atom, useAtomValue, useSetAtom } from 'jotai';

import { Pylon } from '@/lib/Pylon';

const pylonUnreadMessageCountAtom = atom(0);

export const usePylonUnreadMessageReopen = () => {
  const setUnreadMessageCount = useSetAtom(pylonUnreadMessageCountAtom);
  const previousUnreadMessageCount = useRef<number>();

  useEffect(() => {
    window.Pylon?.('onChangeUnreadMessagesCount', (count: number) => {
      setUnreadMessageCount(count);

      if (
        previousUnreadMessageCount.current !== undefined &&
        previousUnreadMessageCount.current !== count
      ) {
        Pylon.show();
      }

      previousUnreadMessageCount.current = count;
    });

    return () => {
      window.Pylon?.('onChangeUnreadMessagesCount', null);
    };
  }, [setUnreadMessageCount]);
};

export const usePylonChat = () => {
  const [showPylonChat, setShowPylonChat] = useState(false);
  const unreadMessageCount = useAtomValue(pylonUnreadMessageCountAtom);

  Pylon.useShowEffect(showPylonChat, setShowPylonChat);

  useEffect(() => {
    if (!showPylonChat) {
      return;
    }

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const pylonChat = document.getElementById('pylon-chat');
      if (pylonChat && !pylonChat.contains(event.target as Node)) {
        setShowPylonChat(false);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showPylonChat]);

  const toggleChat = useCallback(() => {
    setShowPylonChat((prev) => !prev);
  }, []);

  return {
    showPylonChat,
    setShowPylonChat,
    unreadMessageCount,
    toggleChat
  };
};
